<template>
  <div class="details-wrapper">
    <object-header :header-data="objectHeader" />
    <CreatedComment :comment="getClientDetails.created_comment" />
    <div class="details-body">
      <div class="content-panel-wrapper">
        <div v-scroll-spy data-scroll-spy-id="main-tab" class="content-panel">
          <section class="details-section">
            <h1>{{ this.$t('client.labels.details') }}</h1>
            <ClientDetailsForm :client-details="getClientDetails" @formDataChange="dataModified" />
          </section>
          <section class="details-section">
            <h1>{{ this.$t('client.labels.addresses') }}</h1>
            <div class="add">
              <span @click="addClientAddress">+</span>
            </div>
            <div class="address-section">
              <div
                v-for="(address, index) in getClientDetailsAddressList"
                :key="index"
                style="margin:10px"
              >
                <tol-address
                  :address="address"
                  :editable="true"
                  :is-field-read-only="isAddressReadOnly(address)"
                  :default-id="getClientDetails.address_id"
                  @change="updateAddress"
                  @delete="deleteAddress"
                />
              </div>
            </div>
          </section>
          <section class="details-section">
            <h1>{{ this.$t('client.labels.attachments') }}</h1>
            <div v-if="canViewAttachment && getClientDetails && getClientDetails.reference_number">
              <Attachment
                :reference-number="getClientDetails.reference_number"
                owner="CLIENT"
                @attachment-success="refreshAttachments"
              />
              <AttachmentsList
                attachment-type="CLIENT"
                :attachments="clientAttachments"
                :attachment-options="attachmentOptions"
                @delete-attachment="confirmAttachmentDelete"
                @refresh="refreshAttachments"
              />
            </div>
          </section>
          <section class="details-section">
            <h1>{{ this.$t('client.labels.billingMethods') }}</h1>
            <div class="add">
              <span @click="addClientBillingMethod">+</span>
            </div>
            <div class="address-section">
              <template v-if="getClientDetailsBillingIsLoading">
                <tbody>
                  <tr class="billingSpinner">
                    <td :colspan="contactsColumns.length"><vue-simple-spinner size="massive" /></td>
                  </tr>
                </tbody>
              </template>
              <template v-else>
                <div class="billing-block-wrapper">
                  <div
                    v-for="(billingMethod, index) in getClientDetailsBillingMethodsList"
                    :key="index"
                    style="margin:10px;"
                  >
                    <tol-billing-method
                      :billing-method="billingMethod"
                      @delete="deleteBillingMethod"
                      @update="updateBillingMethod"
                    >
                    </tol-billing-method>
                  </div>
                </div>
              </template>

              <Pagination
                v-if="
                  getClientDetailsBillingMethodsList.length > getClientDetailsBillingRecordsPerpage
                "
                :records-per-page="getClientDetailsBillingRecordsPerpage"
                :offset="getClientDetailsBillingRecordsOffset"
                :record-count="getClientDetailsBillingRecordsCount"
                @change="onBillingChangePaginationParams($event)"
              />
            </div>
            <div class="module-action-container">
              <tol-button
                id="save-btn"
                button-type="save"
                label="Save"
                :class="['module-action', { disabled: !isOnScreenDataModified }]"
                @click="doClientSave('ClientEntity')"
              >
              </tol-button>
            </div>
            <update-modal v-if="isProcessBtnClicked">
              <template v-slot:modalData>
                <vue-simple-spinner size="massive" />
              </template>
            </update-modal>
          </section>
          <section class="details-section">
            <h1>{{ this.$t('client.labels.contacts') }}</h1>
            <div class="add">
              <router-link
                :to="{
                  path: '/client-contacts/create',
                  query: { name: getClientDetails.name, id: getClientDetails.id }
                }"
              >
                <span>+</span>
              </router-link>
            </div>
            <ClientDetailsContactActions :client-reference="getClientReference" />
            <tol-table
              :table-data="clientContactList"
              :columns="contactsColumns"
              :show-spinner="getClientDetailsContactsIsLoading"
            >
              <template v-slot:tableHeader>
                <th>{{ $t('client.labels.referenceNumber') }}</th>
                <th>{{ $t('client.labels.firstName') }}</th>
                <th>{{ $t('client.labels.lastName') }}</th>
                <th>{{ $t('client.labels.email') }}</th>
                <th>{{ $t('client.labels.phoneNumber') }}</th>
              </template>
              <template v-slot:referenceNumber="{ value }">
                <router-link :to="'/client-contacts/' + value.referenceNumber">
                  {{ value.referenceNumber }}
                </router-link>
              </template>
            </tol-table>
            <Pagination
              :records-per-page="getClientDetailsContactsRecordsPerpage"
              :offset="getClientDetailsContactsRecordsOffset"
              :record-count="getClientDetailsContactsRecordsCount"
              @change="onContactsChangePaginationParams($event)"
            />
          </section>
          <section class="details-section">
            <h1>{{ this.$t('client.labels.history') }}</h1>

            <tol-table
              :table-data="getClientDetailsHistories"
              :columns="historiesColumns"
              :expandable="true"
              data-label-prefix="client.history"
            >
              <template v-slot:tableHeader>
                <th></th>
                <th>{{ $t('client.history.listing.labels.created_at') }}</th>
                <th>{{ $t('client.history.listing.labels.created_by_name') }}</th>
                <th>{{ $t('client.history.listing.labels.name') }}</th>
                <th>{{ $t('client.history.listing.labels.type') }}</th>
                <th>{{ $t('client.history.listing.labels.credit_check') }}</th>
              </template>
              <template v-slot:created_at="{ value }">
                {{ formatDate(value.created_at) }}
              </template>
              <template v-slot:type="{ value }">
                {{ getDisplayClientType(value.type) }}
              </template>
              <template v-slot:expanded="{ value }">
                <ClientDetailsForm
                  :client-details="getSelectedHistoryRecord(value)"
                  :is-history-record="true"
                />
              </template>
            </tol-table>
          </section>
        </div>
      </div>
    </div>
    <ConfirmModal
      v-if="isDeleteAttachmentModalDisplayed"
      action="DELETE_ATTACHMENT"
      :title="$t('attachments.confirmDeleteTitle')"
      @continue-action="deleteAttachment()"
      @cancel-action="cancelDeletingAttachment()"
    >
      <template v-slot:modalMessage>
        {{ $t('attachments.confirmDelete') }}
      </template>
    </ConfirmModal>
  </div>
</template>
<script>
import { AppActions } from '@/app/app.keys';
import { attachmentService } from '@/app/components/attachments/attachment.service';
import {
  ClientDetailsActions,
  ClientDetailsGetters
} from '@/app/features/clients/features/client-details/client-details.keys';
import { TolErrorsGetters } from '@/app/features/tol-errors/tol-errors.keys';
import ConfirmModal from '@/app/components/ConfirmModal';
import CreatedComment from '@/app/components/CreatedComment';
import TolTable from '@/app/components/TolTable';
import TolAddress from '@/app/components/TolAddress';
import TolBillingMethod from '@/app/components/TolBillingMethod';
import ClientDetailsForm from '@/app/features/clients/features/client-details/components/ClientDetailsForm';
import TolButton from '@/app/components/TolButton';
import Pagination from '@/app/components/Pagination';
import Attachment from '@/app/components/attachments/Attachment';
import ObjectHeader from '@/app/components/ObjectHeader';
import AttachmentsList from '@/app/components/attachments/AttachmentsList';
import ClientDetailsContactActions from '@/app/features/clients/features/client-details/components/ClientDetailsContactActions.vue';
import { mapGetters } from 'vuex';
import format from 'date-fns/format';
import formatDatetime from '@/filters/format-datetime.filter.js';
import UpdateModal from '@/app/components/UpdateModal';

export default {
  components: {
    ClientDetailsForm,
    ConfirmModal,
    TolButton,
    TolTable,
    TolAddress,
    TolBillingMethod,
    Pagination,
    Attachment,
    AttachmentsList,
    ClientDetailsContactActions,
    CreatedComment,
    ObjectHeader,
    UpdateModal
  },
  data() {
    return {
      sidebarMenu: [
        { label: this.$t('client.labels.details') },
        { label: this.$t('client.labels.addresses') },
        { label: this.$t('client.labels.attachments') },
        { label: this.$t('client.labels.billingMethods') },
        { label: this.$t('client.labels.contacts') },
        { label: this.$t('client.labels.history') }
      ],
      isOnScreenDataModified: false,
      isProcessBtnClicked: false,
      contactsColumns: ['referenceNumber', 'firstName', 'lastName', 'email', 'phone1'],
      historiesColumns: ['created_at', 'created_by_name', 'name', 'type', 'credit_check'],
      formData: {},
      attachmentOptions: ['Credit Check Application', 'Credit Check Result', 'Other'],
      isDeleteAttachmentModalDisplayed: false,
      selectedAttachmentId: null
    };
  },
  computed: {
    ...mapGetters([
      ClientDetailsGetters.GetClientDetails,
      ClientDetailsGetters.GetClientDetailsContactsRecordsPerPage,
      ClientDetailsGetters.GetClientDetailsContactsRecordsOffset,
      ClientDetailsGetters.GetClientDetailsContactsRecordsCount,
      ClientDetailsGetters.GetClientDetailsContactsIsLoading,
      ClientDetailsGetters.GetClientDetailsAddressList,
      ClientDetailsGetters.GetClientDetailsBillingRecordsPerPage,
      ClientDetailsGetters.GetClientDetailsBillingRecordsOffset,
      ClientDetailsGetters.GetClientDetailsBillingRecordsCount,
      ClientDetailsGetters.GetClientDetailsBillingMethodsList,
      ClientDetailsGetters.GetClientDetailsBillingIsLoading,
      ClientDetailsGetters.GetClientDetailsHistories
    ]),
    clientContactList() {
      return this.$store.getters[ClientDetailsGetters.GetClientDetailsContactsList];
    },
    getClientContacts() {
      return this.getClientDetails.contacts ? this.getClientDetails.contacts : [];
    },
    currentSaveStatus() {
      return this.$store.getters[TolErrorsGetters.HasErrors] ? 'error' : 'success';
    },
    getClientReference() {
      return this.getClientDetails ? this.getClientDetails.reference_number : '';
    },
    objectHeader() {
      return {
        referenceNumber: this.getClientReference,
        lastModified: `${this.getClientDetails.lastModifiedBy} at ${formatDatetime(
          this.getClientDetails.created_at
        )}`
      };
    },
    /**
     * To be used while implementing
     * address and billing history
     */
    getClientHistories() {
      let clientHistories = [];
      this.getClientDetailsHistories.forEach(element => {
        clientHistories.push(element);
      });
      return clientHistories;
    },
    currentPage() {
      return this.$store.getters[ClientDetailsGetters.GetClientDetailsBillingRecordsOffset];
    },
    currentUser() {
      return this.$store.state.currentUser;
    },
    clientAttachments() {
      return this.$store.getters[ClientDetailsGetters.GetClientAttachments];
    },
    canViewAttachment() {
      return this.currentUser.permissions['CLIENT']['VIEW_ATTACHMENT'];
    }
  },
  watch: {
    $route: {
      immediate: true,
      handler(val) {
        this.$store.dispatch(ClientDetailsActions.RetrieveClientDetails, val.params.id);
      }
    }
  },
  mounted() {
    this.$store.dispatch(AppActions.SetPageTitle, 'Client');
    this.$store.dispatch(ClientDetailsActions.RetrieveClientDetails, this.$route.params.id);
  },
  destroyed() {
    this.$store.dispatch(ClientDetailsActions.ResetClientDetails);
  },
  methods: {
    onBillingChangePaginationParams(event) {
      let clientBillingReference = this.getClientReference;
      this.$store.dispatch(ClientDetailsActions.ClientDetailsBillingPaginationParametersUpdate, {
        paginationParameters: event,
        clientRefNum: clientBillingReference
      });
    },
    onContactsChangePaginationParams(event) {
      let clientContactsReference = this.getClientReference;
      this.$store.dispatch(ClientDetailsActions.ClientDetailsContactsPaginationParametersUpdate, {
        paginationParameters: event,
        clientRefNum: clientContactsReference
      });
    },
    isAddressReadOnly(address) {
      if (
        address &&
        (address.id < 1 ||
          (this.currentUser &&
            this.currentUser.permissions &&
            this.currentUser.permissions['CLIENT_ADDRESS'] &&
            this.currentUser.permissions['CLIENT_ADDRESS']['EDIT']))
      ) {
        return false;
      }
      return true;
    },
    dataModified(formData) {
      this.isOnScreenDataModified = true;
      this.formData = formData;
      this.$store.dispatch(ClientDetailsActions.UpdateClientDetails, this.formData);
    },
    updateAddress(address) {
      this.isOnScreenDataModified = true;
      if (address.name === 'is_default') {
        this.$store.dispatch(ClientDetailsActions.UpdateClientDefaultAddress, address.value);
      } else {
        this.$store.dispatch(ClientDetailsActions.UpdateClientDetailsAddress, address);
      }
    },
    deleteAddress(address) {
      this.isOnScreenDataModified = true;
      this.$store.dispatch(ClientDetailsActions.RemoveAddress, address);
    },
    addClientAddress() {
      this.isOnScreenDataModified = true;
      let address = {
        id: (Math.random() * (0.1 - 0.99)).toFixed(4),
        line1: null,
        line2: null,
        city: null,
        postal_code: null,
        province_abbr: null
      };
      this.$store.dispatch(ClientDetailsActions.AddAddress, address);
    },
    deleteBillingMethod(billingMethod) {
      this.isOnScreenDataModified = true;
      this.$store.dispatch(ClientDetailsActions.RemoveClientDetailsBillingMethod, billingMethod);
    },
    addClientBillingMethod() {
      this.isOnScreenDataModified = true;
      let billingMethod = {
        id: (Math.random() * (0.1 - 0.99)).toFixed(4),
        type: 'Type',
        attention: null,
        email: null,
        other: null,
        is_default: false,
        address_id: null,
        user_id: null,
        created_comment: '',
        client_id: this.getClientDetails.id,
        clientAddress: ''
      };
      this.$store.dispatch(ClientDetailsActions.AddClientDetailsBillingMethod, billingMethod);
    },
    updateBillingMethod(billingMethod) {
      this.isOnScreenDataModified = true;
      this.$store.dispatch(ClientDetailsActions.UpdateClientDetailsBillingMethod, billingMethod);
    },
    getSelectedHistoryRecord(clientDetails) {
      let returnValue = this.getClientHistories.filter(
        historyRecord => historyRecord.id === clientDetails.id
      )[0];
      return returnValue;
    },
    async doClientSave(section) {
      this.isProcessBtnClicked = true;
      await this.$store.dispatch(ClientDetailsActions.SaveClientDetails, section);
      window.setTimeout(() => {
        this.isProcessBtnClicked = false;
        this.isOnScreenDataModified = false;
      }, 2000);
    },
    formatDate(date) {
      return date ? format(date, 'YYYY-MM-DD HH:mm') : null;
    },
    getDisplayClientType(clientType) {
      if (clientType === 'REGULAR') {
        clientType = 'Regular';
      } else if (clientType === 'END_CLIENT') {
        clientType = 'End Client';
      } else {
        clientType = 'Both';
      }
      return clientType;
    },
    confirmAttachmentDelete(attachmentId) {
      this.selectedAttachmentId = attachmentId;
      this.isDeleteAttachmentModalDisplayed = true;
    },
    async deleteAttachment() {
      await attachmentService.deleteAttachment(this.selectedAttachmentId);
      this.isDeleteAttachmentModalDisplayed = false;
      this.selectedAttachmentId = null;
      this.refreshAttachments();
    },
    cancelDeletingAttachment() {
      this.isDeleteAttachmentModalDisplayed = false;
      this.selectedAttachmentId = null;
    },
    refreshAttachments() {
      this.$store.dispatch(ClientDetailsActions.RetrieveClientAttachments, this.$route.params.id);
    }
  }
};
</script>
<style lang="scss" scoped>
@import '@/app/styles/colors.scss';
@import '@/app/styles/variables.scss';
@import '@/app/styles/action-buttons.scss';

.billing-block-wrapper {
  width: 100%;
  display: flex;
  flex-wrap: wrap;
}

.details-wrapper {
  display: flex;
  flex-direction: column;

  background-color: $background-level-2;
  .details-header {
    display: flex;

    padding: $details-padding;
    border-bottom: 2px solid $row-border;
    justify-content: space-between;
  }
  .details-body {
    position: relative;
    display: flex;
    justify-content: center;
    border-right: 4px solid rgba(255, 255, 255, 0);
    &.modified {
      border-right: 4px solid $modified-form;
    }

    .content-panel-wrapper {
      flex: 0 85%;
      .content-panel {
        display: block;
        background-color: $white;
        padding: 60px;
      }
    }

    .details-section {
      padding-bottom: 25px;

      h1 {
        border-bottom: 1px solid $row-border;
      }

      .address-section {
        display: flex;
        flex-wrap: wrap;
      }
    }
  }

  .empty {
    display: flex;
    padding: 60px;
  }
}

.add {
  display: flex;
  justify-content: flex-end;
  span {
    display: inline-block;
    font-size: 1.2em;
    font-weight: bold;
    width: 20px;
    height: 20px;
    line-height: 20px;
    border-radius: 100%;
    text-align: center;
    &:hover {
      cursor: pointer;
      background-color: $light-grey-2;
    }
    &.history {
      display: none;
    }
  }
}

.action-block {
  width: 100%;
  margin-left: 90%;
  margin-top: 5px;
}

.billingSpinner {
}
</style>
